/**
 * Utilidades para hexágonos en coordenadas axiales (q, r)
 * Orientación: pointy-top
 */

export const SQRT_3 = Math.sqrt(3)

/**
 * Convierte axial (q, r) a coordenadas de píxel
 * pointy-top: x = size * sqrt(3) * (q + r/2); y = size * 3/2 * r
 */
export function axialToPixel(q, r, size) {
  const x = size * SQRT_3 * (q + r / 2)
  const y = size * (3 / 2) * r
  return { x, y }
}

/**
 * Devuelve los 6 vértices del hex centrado en (cx, cy)
 */
export function hexPolygonPoints(cx, cy, size) {
  const points = []
  for (let i = 0; i < 6; i++) {
    const angleDeg = 60 * i - 30 // pointy-top
    const angleRad = (Math.PI / 180) * angleDeg
    const px = cx + size * Math.cos(angleRad)
    const py = cy + size * Math.sin(angleRad)
    points.push([px, py])
  }
  return points
}

/**
 * Serializa puntos a string para atributo "points" de SVG
 */
export function toPointsAttr(points) {
  return points.map(([x, y]) => `${x},${y}`).join(' ')
}

/**
 * Color por estado (fondo) y borde
 */
export const STATUS_COLORS = {
  Neutral: { fill: '#9E9E9E22', stroke: '#9E9E9E' },
  Paz: { fill: '#4CAF5022', stroke: '#2E7D32' },
  Tension: { fill: '#FF980022', stroke: '#EF6C00' },
  Guerra: { fill: '#F4433622', stroke: '#C62828' },
  Ocupado: { fill: '#3F51B522', stroke: '#283593' },
  Disputa: { fill: '#FF572222', stroke: '#BF360C' },
}
// Normalización de estado (acepta variantes legacy/inglés) y mapeo a colores
export function normalizeStatus(status) {
  const t = String(status || '').trim().toLowerCase()
  switch (t) {
    // Paz
    case 'paz':
    case 'peace':
    case 'controlled':
    case 'controlado':
      return 'Paz'
    // Tensión
    case 'tension':
    case 'tensión':
    case 'tense':
      return 'Tension'
    // Guerra
    case 'guerra':
    case 'war':
      return 'Guerra'
    // Ocupado
    case 'ocupado':
    case 'occupied':
      return 'Ocupado'
    // Disputa/Contestado
    case 'disputa':
    case 'contested':
    case 'disputed':
      return 'Disputa'
    // Neutral (por defecto)
    case 'neutral':
    default:
      return 'Neutral'
  }
}

export function getStatusColors(status) {
  const key = normalizeStatus(status)
  return STATUS_COLORS[key] || STATUS_COLORS.Neutral
}

/**
 * Paleta fija por Imperio/Shogunato
 * Claves soportadas: slug y nombre en minúsculas
 */
export const IMPERIAL_COLORS = {
  // Slugs (derivan de nombre: lower + espacios -> '-')
  'imperio-del-fuego': '#F44336',      // Rojo
  'imperio-de-masadora': '#FF9800',    // Naranja
  'imperio-de-meshokan': '#FFEB3B',    // Amarillo
  'republica-de-jiyu': '#00BCD4',      // Cyan

  // Nombres normalizados (por robustez)
  'imperio del fuego': '#F44336',
  'imperio de masadora': '#FF9800',
  'imperio de meshokan': '#FFEB3B',
  'república de jiyu': '#00BCD4',
  'republica de jiyu': '#00BCD4',

  // Tokens directos (por si llegan valores abreviados o distintos)
  'fuego': '#F44336',
  'masadora': '#FF9800',
  'meshokan': '#FFEB3B',
  'jiyu': '#00BCD4',
}

/**
 * Hash de string a color HSL estable
 */
export function hashColor(str, s = 55, l = 45) {
  if (!str) return '#607D8B'
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash |= 0
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue} ${s}% ${l}%)`
}

/**
 * Determina colores finales priorizando colores provistos por backend:
 * - empireColor (relleno Imperio)
 * - hanColor (relleno Han si no hay Imperio)
 * - clanColor (borde Han si está presente)
 * Fallbacks: heurística IMPERIAL_COLORS por slug/nombre, hash por clan y colores por estado.
 */
export function colorForCell(cell) {
  // Normalizar estado para evitar caer en "Neutral" por variantes de texto
  const byStatus = getStatusColors(cell?.status)

  // Si backend provee color de imperio, usarlo como fill prioritario
  const normalizeHex = (hex) => {
    if (!hex || typeof hex !== 'string') return null
    const m = hex.trim().match(/^#([0-9a-f]{6}|[0-9a-f]{8})$/i)
    return m ? `#${m[1]}` : null
  }
  const withAlpha = (hex, aa = '99') => {
    if (!hex) return null
    // si ya trae alpha, dejarlo
    if (/^#([0-9a-f]{8})$/i.test(hex)) return hex
    if (/^#([0-9a-f]{6})$/i.test(hex)) return `${hex}${aa}`
    return null
  }

  // Preferencias de fill (Imperio > Han > fallback)
  let empireFill = null
  let hanFill = null
  let stroke = byStatus.stroke

  const beEmpire = normalizeHex(cell?.empireColor)
  if (beEmpire) {
    empireFill = withAlpha(beEmpire, '99')
    stroke = beEmpire
  }

  // Si no hay color de imperio, intentar heurística por slug/nombre
  if (!empireFill) {
    const norm = (s) => (s && s.normalize ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s)
    const toKey = (s) => String(s || '').toLowerCase().trim()
    const lookupImperial = (k) => {
      if (!k) return null
      const key = toKey(k)
      return IMPERIAL_COLORS[key] || IMPERIAL_COLORS[toKey(norm(key))] || null
    }
    const candidates = [
      cell?.empireSlug,
      cell?.empireName,
      cell?.territorySlug,
      cell?.territoryName,
    ].filter(Boolean)

    let empBase = null
    for (const c of candidates) {
      empBase = lookupImperial(c)
      if (empBase) break
    }
    if (!empBase) {
      const joined = toKey(norm(candidates.join(' ')))
      if (joined.includes('fuego')) empBase = IMPERIAL_COLORS['fuego']
      else if (joined.includes('masadora')) empBase = IMPERIAL_COLORS['masadora']
      else if (joined.includes('meshokan')) empBase = IMPERIAL_COLORS['meshokan']
      else if (joined.includes('jiyu')) empBase = IMPERIAL_COLORS['jiyu']
    }
    if (empBase) {
      empireFill = withAlpha(empBase, '99')
      stroke = empBase
    }
  }

  // Han fill (si no hay imperio)
  if (!empireFill) {
    const beHan = normalizeHex(cell?.hanColor)
    if (beHan) {
      hanFill = withAlpha(beHan, '99')
    }
  }

  // Clan stroke (si disponible)
  const beClan = normalizeHex(cell?.clanColor)
  if (beClan) {
    stroke = beClan
  } else if (cell?.clanSlug) {
    // fallback determinista por clan
    stroke = hashColor(String(cell.clanSlug))
  }

  // Resultado final
  return {
    fill: empireFill || hanFill || byStatus.fill,
    stroke: stroke || byStatus.stroke,
  }
}

/**
 * Caja envolvente aproximada de un conjunto de celdas para centrar/fit
 */
export function computeBounds(cells, size) {
  if (!Array.isArray(cells) || cells.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 }
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const c of cells) {
    const { x, y } = axialToPixel(Number(c.q), Number(c.r), size)
    // considerar radio del hex
    const w = SQRT_3 * size
    const h = size * 2
    minX = Math.min(minX, x - w / 2)
    maxX = Math.max(maxX, x + w / 2)
    minY = Math.min(minY, y - h / 2)
    maxY = Math.max(maxY, y + h / 2)
  }
  return { minX, minY, maxX, maxY }
}
// === Editor helpers: vecinos, distancia, brocha (axial) ===

/**
 * Direcciones en axial (pointy-top)
 * 0..5: E, NE, NW, W, SW, SE
 */
export const axialDirections = [
  [1, 0],   // E
  [1, -1],  // NE
  [0, -1],  // NW
  [-1, 0],  // W
  [-1, 1],  // SW
  [0, 1],   // SE
]

/**
 * Índice de arista del polígono (vértices i → i+1) que enfrenta cada dirección axial.
 * Con vértices a 60*i-30°, las aristas van E, SE, SW, W, NW, NE.
 */
export const AXIAL_DIR_TO_EDGE = [0, 5, 4, 3, 2, 1]

/**
 * Extremos [ [x1,y1], [x2,y2] ] de la arista `edgeIndex` (0..5) del hex en (cx,cy).
 */
export function hexEdgeEndpoints(cx, cy, size, edgeIndex) {
  const verts = hexPolygonPoints(cx, cy, size)
  const i = ((edgeIndex % 6) + 6) % 6
  return [verts[i], verts[(i + 1) % 6]]
}

/**
 * Une aristas sueltas en paths SVG continuos (varios anillos/polilíneas si hace falta).
 * @param {Array<{a:[number,number], b:[number,number]}>} edges
 * @returns {string[]} atributos `d` listos para <path>
 */
export function edgesToSvgPathD(edges, precision = 100) {
  if (!Array.isArray(edges) || edges.length === 0) return []

  const keyOf = (p) => {
    const x = Math.round(Number(p[0]) * precision)
    const y = Math.round(Number(p[1]) * precision)
    return `${x},${y}`
  }

  /** @type {Map<string, [number, number]>} */
  const pointByKey = new Map()
  /** @type {Map<string, Array<{id:number, other:string, to:[number,number]}>>} */
  const adj = new Map()
  const used = new Set()

  const addLink = (fromKey, toKey, toPt, id) => {
    if (!adj.has(fromKey)) adj.set(fromKey, [])
    adj.get(fromKey).push({ id, other: toKey, to: toPt })
  }

  edges.forEach((e, id) => {
    if (!e?.a || !e?.b) return
    const ka = keyOf(e.a)
    const kb = keyOf(e.b)
    if (ka === kb) return
    if (!pointByKey.has(ka)) pointByKey.set(ka, e.a)
    if (!pointByKey.has(kb)) pointByKey.set(kb, e.b)
    addLink(ka, kb, e.b, id)
    addLink(kb, ka, e.a, id)
  })

  const takeNext = (fromKey) => {
    const list = adj.get(fromKey) || []
    for (const link of list) {
      if (!used.has(link.id)) return link
    }
    return null
  }

  const degreeUnused = (k) => (adj.get(k) || []).filter((l) => !used.has(l.id)).length

  const paths = []
  const startCandidates = () => {
    const keys = [...adj.keys()]
    const odds = keys.filter((k) => degreeUnused(k) % 2 === 1)
    return odds.length ? odds : keys.filter((k) => degreeUnused(k) > 0)
  }

  while (true) {
    const starts = startCandidates()
    if (!starts.length) break
    const startKey = starts[0]
    const first = takeNext(startKey)
    if (!first) break
    used.add(first.id)

    const startPt = pointByKey.get(startKey) || first.to
    const pts = [startPt, first.to]
    let currKey = first.other

    while (true) {
      const next = takeNext(currKey)
      if (!next) break
      used.add(next.id)
      pts.push(next.to)
      currKey = next.other
      if (currKey === startKey) break
    }

    if (pts.length < 2) continue
    const closed = keyOf(pts[0]) === keyOf(pts[pts.length - 1])
    let d = `M ${pts[0][0]} ${pts[0][1]}`
    for (let i = 1; i < pts.length; i++) {
      d += ` L ${pts[i][0]} ${pts[i][1]}`
    }
    if (closed) d += ' Z'
    paths.push(d)
  }

  return paths
}

/**
 * Vecino axial en dirección dir (0..5)
 */
export function axialNeighbor(q, r, dir) {
  const d = axialDirections[dir % 6]
  return [q + d[0], r + d[1]]
}

/**
 * Distancia hex axial entre (q1,r1) y (q2,r2)
 */
export function axialDistance(q1, r1, q2, r2) {
  // Convertir a cubic: x=q, z=r, y=-x-z; distance = (|dx|+|dy|+|dz|)/2
  const x1 = q1
  const z1 = r1
  const y1 = -x1 - z1
  const x2 = q2
  const z2 = r2
  const y2 = -x2 - z2
  return (Math.abs(x1 - x2) + Math.abs(y1 - y2) + Math.abs(z1 - z2)) / 2
}

/**
 * Conjunto de celdas dentro de radio (inclusive) alrededor de (q,r)
 * radio 0 => solo el centro; radio 1 => anillo + centro; etc.
 * Devuelve Array de {q,r}
 */
export function axialRange(q, r, radius) {
  const res = []
  for (let dq = -radius; dq <= radius; dq++) {
    for (let dr = Math.max(-radius, -dq - radius); dr <= Math.min(radius, -dq + radius); dr++) {
      const q2 = q + dq
      const r2 = r + dr
      res.push({ q: q2, r: r2 })
    }
  }
  return res
}

/**
 * Genera un rectángulo simple de celdas axiales (útil para editores)
 * Nota: No es un rectángulo perfecto en métrica hex, pero sirve como malla de fondo.
 */
export function generateAxialRect(minQ, maxQ, minR, maxR) {
  const list = []
  for (let q = minQ; q <= maxQ; q++) {
    for (let r = minR; r <= maxR; r++) {
      list.push({ q, r })
    }
  }
  return list
}